import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Zap, Plus, Edit2, Trash2, X, Search, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const substitute = (body, ctx) => {
  return (body || '')
    .replace(/\{\{carrier_name\}\}/g, ctx.carrier_name || 'Driver')
    .replace(/\{\{load_ref\}\}/g, ctx.load_ref || '—')
    .replace(/\{\{dispatcher_name\}\}/g, ctx.dispatcher_name || 'Dispatch');
};

export const QuickRepliesPopover = ({ onInsert, context, currentUserName }) => {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | reply object

  const ctx = { ...context, dispatcher_name: currentUserName || 'Dispatch' };

  const fetchReplies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-replies`);
      setReplies(res.data?.replies || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && replies.length === 0) fetchReplies();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group by category
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? replies.filter((r) => r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q))
      : replies;
    const byCat = {};
    filtered.forEach((r) => {
      const cat = r.category || 'General';
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(r);
    });
    return Object.entries(byCat).sort(([a], [b]) => a.localeCompare(b));
  }, [replies, query]);

  const pick = async (reply) => {
    const text = substitute(reply.body, ctx);
    onInsert?.(text);
    setOpen(false);
    // fire-and-forget usage counter
    axios.post(`${API}/quick-replies/${reply.id}/use`).catch(() => {});
  };

  const remove = async (reply) => {
    if (!window.confirm(`Delete "${reply.title}"?`)) return;
    try {
      await axios.delete(`${API}/quick-replies/${reply.id}`);
      setReplies((arr) => arr.filter((r) => r.id !== reply.id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bg-amber-500 hover:bg-amber-600 text-white px-3 flex items-center justify-center transition-colors"
        title="Quick replies"
        data-testid="quick-replies-toggle"
      >
        <Zap className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute bottom-12 right-0 w-[380px] max-w-[calc(100vw-2rem)] max-h-[440px] bg-white border border-zinc-200 shadow-2xl z-50 flex flex-col"
          data-testid="quick-replies-popover"
        >
          {/* Header */}
          <div className="bg-zinc-900 text-white px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-black uppercase tracking-wider">Quick Replies</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEditing('new')}
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-red-600 hover:bg-red-700 flex items-center gap-1"
                data-testid="quick-replies-new"
              >
                <Plus className="w-3 h-3" />New
              </button>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Editor */}
          {editing ? (
            <ReplyEditor
              reply={editing === 'new' ? null : editing}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); fetchReplies(); }}
            />
          ) : (
            <>
              {/* Search */}
              <div className="border-b border-zinc-100 px-3 py-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search replies…"
                    className="w-full bg-zinc-50 border border-zinc-200 text-xs pl-8 pr-2 py-1.5 focus:outline-none focus:border-red-600"
                    data-testid="quick-replies-search"
                  />
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto" data-testid="quick-replies-list">
                {loading ? (
                  <div className="py-8 text-center text-xs text-zinc-400">Loading…</div>
                ) : grouped.length === 0 ? (
                  <div className="py-8 text-center text-xs text-zinc-400">No replies found.</div>
                ) : grouped.map(([cat, items]) => (
                  <div key={cat}>
                    <div className="px-3 py-1.5 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-500">{cat}</div>
                    {items.map((r) => (
                      <div
                        key={r.id}
                        className="group px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 cursor-pointer flex items-start gap-2"
                        onClick={() => pick(r)}
                        data-testid={`quick-reply-${r.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-zinc-900 truncate">{r.title}</div>
                          <div className="text-[11px] text-zinc-500 truncate leading-snug">{substitute(r.body, ctx)}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          {r.usage_count > 0 && (
                            <span className="text-[9px] text-zinc-400 px-1 tabular-nums" title={`Used ${r.usage_count} times`}>{r.usage_count}×</span>
                          )}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {r.can_edit && (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                                  className="text-zinc-400 hover:text-zinc-900 p-1"
                                  title="Edit"
                                ><Edit2 className="w-3 h-3" /></button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); remove(r); }}
                                  className="text-zinc-400 hover:text-red-600 p-1"
                                  title="Delete"
                                ><Trash2 className="w-3 h-3" /></button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="px-3 py-1.5 border-t border-zinc-100 bg-zinc-50 text-[10px] text-zinc-500">
                Tip: <code className="bg-white px-1">{'{{carrier_name}}'}</code>, <code className="bg-white px-1">{'{{load_ref}}'}</code>, <code className="bg-white px-1">{'{{dispatcher_name}}'}</code> auto-fill.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ReplyEditor = ({ reply, onCancel, onSaved }) => {
  const [title, setTitle] = useState(reply?.title || '');
  const [body, setBody] = useState(reply?.body || '');
  const [category, setCategory] = useState(reply?.category || 'General');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    if (!title.trim() || !body.trim()) { setErr('Title and body are required'); return; }
    setSaving(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), category: category.trim() || 'General' };
      if (reply?.id) {
        await axios.patch(`${API}/quick-replies/${reply.id}`, payload);
      } else {
        await axios.post(`${API}/quick-replies`, payload);
      }
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 space-y-2 overflow-y-auto" data-testid="quick-replies-editor">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Title</label>
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
          className="w-full bg-zinc-50 border border-zinc-200 text-sm px-2 py-1.5 focus:outline-none focus:border-red-600"
          data-testid="qr-editor-title" placeholder="e.g. Standard pay terms"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Category</label>
        <input
          type="text" value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60}
          className="w-full bg-zinc-50 border border-zinc-200 text-sm px-2 py-1.5 focus:outline-none focus:border-red-600"
          data-testid="qr-editor-category" placeholder="e.g. Payment"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Body</label>
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={2000}
          className="w-full bg-zinc-50 border border-zinc-200 text-sm px-2 py-1.5 focus:outline-none focus:border-red-600 resize-none"
          data-testid="qr-editor-body" placeholder="Use {{carrier_name}}, {{load_ref}}, {{dispatcher_name}} as variables…"
        />
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={save} disabled={saving}
          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-zinc-300 text-white text-xs font-bold uppercase tracking-wider py-2 flex items-center justify-center gap-1"
          data-testid="qr-editor-save"
        ><Check className="w-3 h-3" />Save</button>
        <button
          onClick={onCancel}
          className="px-3 bg-zinc-200 hover:bg-zinc-300 text-zinc-900 text-xs font-bold uppercase tracking-wider"
          data-testid="qr-editor-cancel"
        >Cancel</button>
      </div>
    </div>
  );
};

export default QuickRepliesPopover;
